/* eslint-disable @typescript-eslint/no-explicit-any */
import { DivergenceStatusEnum } from "@/enum/divergence-status";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { Button } from "@/components/ui/button";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";
import { PERMISSION_DENIED_LABEL } from "@/constants/permission";
import {
  consumeOwnDecision,
  rememberOwnDecisions,
} from "../../utils/own-decisions";
import { Check, Undo2, X } from "lucide-react";
import { toast } from "components/ui/use-toast";

import { Meta } from "@/@types/paginated";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { EmptyTable } from "components/Datatable/components/EmptyTable";
import { useFilterState } from "../../filter/hooks/filterState";
import { useBatchUpdateDivergenceStatusMutation } from "@/services/divergence";
import { useBatchUpdateNsuStatusMutation } from "@/services/conciliation/nsu";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  BatchActionModal,
  BatchDecisionItem,
  BatchItem,
} from "./batch-action-modal";
import {
  BATCH_STATUS_MAP,
  DATA_TYPES,
  BATCH_ACTION_LABELS,
  DIVERGENCE_DECISION_STATUS,
  SELECTION_LABELS,
  TABLE_STATES,
  EMPTY_STATE_MESSAGES,
  type BatchActionType
} from "../../utils/constants";
import {
  IDENTITY_QUEUE_LABELS,
  MANAGE_LOSS_MESSAGES,
} from "../../utils/messages";
import { IdentityQueueModal } from "../divergence/identity-queue-modal";
import {
  IdentityQueueEntry,
  IdentitySplit,
  installmentsByIdOf,
  splitIdentitySkips,
} from "../../utils/identity-queue";
import { BatchResultSkip } from "../../utils/batch-result-summary";

const STICKY_ACTIONS_CLASS: string = "sticky right-0 bg-white z-10 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]";

const STICKY_COLUMN_IDS: Record<string, string | undefined> = {
  actions: STICKY_ACTIONS_CLASS,
};
import { summarizeSkipped } from "../../utils/batch-result-summary";
import { BatchReversalModal } from "../divergence/batch-reversal-modal";
import { buildBatchPreflight } from "../../utils/batch-preflight";
import { BatchApprovalModal } from "../divergence/batch-approval-modal";
import { BATCH_APPROVAL_LABELS } from "../../utils/messages";
import { REVERSAL_LABELS, REVERSAL_TOASTS } from "../../utils/reversal-messages";
import {
  ReversibleRow,
  canDecideRow,
  canReverseRow,
} from "../../utils/reversal-availability";
import {
  ReversibleSource,
  ReversibleTarget,
  toReversible,
} from "../../utils/to-reversible";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  meta: any;
  isLoading?: boolean;
  dataType?: "divergence" | "nsu";
  openCycle?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  meta,
  isLoading = false,
  dataType = "divergence",
  openCycle,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const [batchUpdateDivergence, { isLoading: isUpdatingDivergence }] = useBatchUpdateDivergenceStatusMutation();
  const [batchUpdateNsu, { isLoading: isUpdatingNsu }] = useBatchUpdateNsuStatusMutation();
  const { handleModal } = useDialogModal();

  const isUpdating = isUpdatingDivergence || isUpdatingNsu;
  const selectedCount = Object.keys(rowSelection).length;
  const { canUpdate } = useAssignmentAbilities(AssignmentsEnum.DIVERGENCE);

  const visibleIds: string = React.useMemo(
    (): string =>
      data
        .map((row: TData): string =>
          String((row as unknown as { id: number | string }).id)
        )
        .join(","),
    [data]
  );

  React.useEffect((): void => {
    setRowSelection({});
  }, [visibleIds]);

  const hasActiveFilters = () => {
    return !!(
      (filters.conciliation_date_range?.start || filters.conciliation_date_range?.end) ||
      (filters.store_code && filters.store_code.length > 0) ||
      (filters.analyst && filters.analyst.length > 0) ||
      (filters.status && filters.status.length > 0) ||
      (filters.justification_id && filters.justification_id.length > 0) ||
      (filters.type && filters.type.length > 0) ||
      (filters.payroll_voucher_status &&
        filters.payroll_voucher_status.length > 0) ||
      (filters.search && filters.search.length > 0)
    );
  };

  const openIdentityQueue = (
    action: BatchActionType,
    entries: IdentityQueueEntry<BatchItem>[],
    blockedCount: number = 0
  ): void => {
    setRowSelection(
      Object.fromEntries(
        entries.map(
          (entry: IdentityQueueEntry<BatchItem>): [string, boolean] => [
            String(entry.item.id),
            true,
          ]
        )
      )
    );

    handleModal({
      isOpen: true,
      title: IDENTITY_QUEUE_LABELS.TITLE,
      element: (
        <IdentityQueueModal
          entries={entries}
          blockedCount={blockedCount}
          action={DIVERGENCE_DECISION_STATUS[action]}
          onFinished={(): void => {
            setRowSelection({});
            handleModal({ isOpen: false });
          }}
        />
      ),
    });
  };

  const entriesForQueue = (
    decisions: BatchDecisionItem[],
    rows: BatchItem[]
  ): IdentityQueueEntry<BatchItem>[] => {
    const rowById: Map<number, BatchItem> = new Map(
      rows.map((row: BatchItem): [number, BatchItem] => [row.id, row])
    );

    return decisions.flatMap(
      (decision: BatchDecisionItem): IdentityQueueEntry<BatchItem>[] => {
        const item: BatchItem | undefined = rowById.get(decision.id);

        if (!item) return [];

        return [{ item, installmentsCount: decision.installments_count }];
      }
    );
  };

  const executeBatchAction = async (
    action: BatchActionType,
    decisions: BatchDecisionItem[],
    rows: BatchItem[]
  ) => {

    if (
      dataType === DATA_TYPES.DIVERGENCE &&
      DIVERGENCE_DECISION_STATUS[action] === DivergenceStatusEnum.APPROVED
    ) {
      openIdentityQueue(action, entriesForQueue(decisions, rows));
      return;
    }

    const status = BATCH_STATUS_MAP[action][dataType];

    const decidableIds: number[] = decisions.map(
      (decision: BatchDecisionItem): number => decision.id
    );

    const items = decisions.map((decision: BatchDecisionItem) => ({
      ...decision,
      status,
    }));

    let processed: number = items.length;
    let skippedSummary = "";
    let identityQueue: IdentityQueueEntry<BatchItem>[] = [];

    rememberOwnDecisions(decidableIds);

    try {
      if (dataType === DATA_TYPES.DIVERGENCE) {
        const result = await batchUpdateDivergence({ items }).unwrap();
        const skipped: BatchResultSkip[] = result?.skipped ?? [];

        processed = result?.updated?.length ?? items.length;

        skipped.forEach((skip: BatchResultSkip): void => {
          if (skip.id === null) return;
          consumeOwnDecision(skip.id);
        });

        const split: IdentitySplit<BatchItem> = splitIdentitySkips(
          skipped,
          rows,
          installmentsByIdOf(decisions)
        );

        identityQueue = split.queue;
        skippedSummary = summarizeSkipped(split.remaining);
      } else {
        await batchUpdateNsu({ items }).unwrap();
      }

      toast(
        skippedSummary
          ? MANAGE_LOSS_MESSAGES.BATCH_ACTION.PARTIAL(
              processed,
              skippedSummary,
              action
            )
          : MANAGE_LOSS_MESSAGES.BATCH_ACTION.SUCCESS(processed, action)
      );

      if (identityQueue.length > 0) {
        openIdentityQueue(action, identityQueue);
        return;
      }

      if (processed > 0) setRowSelection({});

      handleModal({ isOpen: false });
    } catch (error) {
      decidableIds.forEach(consumeOwnDecision);

      toast(MANAGE_LOSS_MESSAGES.BATCH_ACTION.ERROR(action));
    }
  };

  const openBatchReversal = (): void => {
    if (!openCycle) {
      toast(REVERSAL_TOASTS.NO_OPEN_CYCLE);
      return;
    }

    const reversible: ReversibleTarget[] = table
      .getSelectedRowModel()
      .rows.map(row => row.original as ReversibleRow & ReversibleSource)
      .filter(canReverseRow)
      .map(toReversible);

    if (reversible.length === 0) {
      toast(MANAGE_LOSS_MESSAGES.BATCH_ACTION.NO_REVERSIBLE_SELECTION);
      return;
    }

    handleModal({
      isOpen: true,
      title: REVERSAL_LABELS.BATCH_TITLE,
      element: (
        <BatchReversalModal
          divergences={reversible}
          openCycle={openCycle}
          onReversed={() => setRowSelection({})}
        />
      ),
    });
  };

  const handleBatchAction = (action: BatchActionType) => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) {
      toast(MANAGE_LOSS_MESSAGES.BATCH_ACTION.NO_SELECTION);
      return;
    }

    const hasDecidable: boolean =
      dataType !== DATA_TYPES.DIVERGENCE ||
      selectedRows.some(row => canDecideRow(row.original as ReversibleRow));

    if (!hasDecidable) {
      toast(MANAGE_LOSS_MESSAGES.BATCH_ACTION.NO_DECIDABLE_SELECTION);
      return;
    }

    const selectedItems = selectedRows.map(row => row.original);

    if (
      dataType === DATA_TYPES.DIVERGENCE &&
      DIVERGENCE_DECISION_STATUS[action] === DivergenceStatusEnum.APPROVED
    ) {
      const preflight = buildBatchPreflight(
        selectedItems as BatchItem[],
        DivergenceStatusEnum.APPROVED
      );

      if (preflight.valid.length === 0) {
        toast(MANAGE_LOSS_MESSAGES.BATCH_ACTION.NO_DECIDABLE_SELECTION);
        return;
      }

      setRowSelection(
        Object.fromEntries(
          preflight.valid.map((item): [string, boolean] => [
            String(item.id),
            true,
          ])
        )
      );

      handleModal({
        isOpen: true,
        title: BATCH_APPROVAL_LABELS.TITLE,
        element: (
          <BatchApprovalModal
            items={preflight.valid as BatchItem[]}
            blockedCount={preflight.skipped.length}
            onFinished={(): void => {
              setRowSelection({});
              handleModal({ isOpen: false });
            }}
          />
        ),
      });

      return;
    }

    const actionTitle = BATCH_ACTION_LABELS[action].modalTitle;

    handleModal({
      isOpen: true,
      title: actionTitle,
      element: (
        <BatchActionModal
          action={action}
          items={selectedItems as BatchItem[]}
          dataType={dataType}
          onConfirm={(decisions: BatchDecisionItem[]) =>
            executeBatchAction(action, decisions, selectedItems as BatchItem[])
          }
        />
      ),
    });
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    getRowId: (row: TData): string =>
      String((row as unknown as { id: number | string }).id),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true, 
    pageCount: meta?.lastPage || 1, 
  });

  return (
    <div className="space-y-4">
      <DataTableToolbar />
      {selectedCount > 0 && (
        <div className="sticky top-0 z-20 bg-[#F9FBFC] border rounded-xl py-4 px-4 mt-6 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#613F4F]">
              {selectedCount} {selectedCount === 1 ? SELECTION_LABELS.single : SELECTION_LABELS.plural}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBatchAction("approve")}
                disabled={isUpdating || !canUpdate}
                title={canUpdate ? undefined : PERMISSION_DENIED_LABEL}
              >
                <Check className="mr-2 h-4 w-4" />
                {BATCH_ACTION_LABELS.approve.buttonText}
              </Button>
              <Button
                size="sm"
                onClick={() => handleBatchAction("reject")}
                disabled={isUpdating || !canUpdate}
                title={canUpdate ? undefined : PERMISSION_DENIED_LABEL}
                className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
              >
                <X className="mr-2 h-4 w-4" />
                {BATCH_ACTION_LABELS.reject.buttonText}
              </Button>
              {dataType === DATA_TYPES.DIVERGENCE && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openBatchReversal}
                  disabled={isUpdating || !canUpdate}
                  title={canUpdate ? undefined : PERMISSION_DENIED_LABEL}
                >
                  <Undo2 className="mr-2 h-4 w-4" />
                  {REVERSAL_LABELS.BATCH_MENU}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={STICKY_COLUMN_IDS[header.id]}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody >
            {isLoading ? (
              <TableLoad colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && TABLE_STATES.SELECTED}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={STICKY_COLUMN_IDS[cell.column.id]}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {hasActiveFilters() ? (
                    <div className="text-muted-foreground">
                      {EMPTY_STATE_MESSAGES.noResults}
                    </div>
                  ) : (
                    <EmptyTable />
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} meta={meta as Meta} />
    </div>
  );
}
